import { environment } from './config.service';

/** True when the AppConfig version is 'future' — unlocks URL tampering tolerance */
export function isFutureVersion(): boolean {
  return (environment?.version ?? '').toLowerCase() === 'future';
}

/**
 * [CurveBeamAI] localStorage key for auth data – aligned with web-connect-service.
 * Exported so cross-tab listeners can match `StorageEvent.key` against the same constant
 * rather than re-hardcoding the string.
 */
export const AUTH_DATA_KEY = 'ls.authData';

/** [CurveBeamAI] sessionStorage key for original query params – tampering detection */
export const ORIGINAL_QUERY_PARAMS_KEY = 'cbai.originalQueryParams';

/** [CurveBeamAI] Path segment for the config-error page */
export const CONFIG_ERROR_PATH = '/config-error';

/** [CurveBeamAI] Path segment for invalid-source page */
export const INVALID_SOURCE_PATH = '/invalid-source';

/**
 * [WTD-1826] Path segment for the session-expired page.
 *
 * Separate from invalid-source because the two say opposite things to a user. Invalid-source means
 * "you arrived without a session" and asks them to launch from DMP. This means "you had one and it
 * ended" - and showing them the first for the second told a radiologist whose session timed out
 * mid-read to do the thing they had already done.
 */
export const SESSION_EXPIRED_PATH = '/session-expired';

/** [CurveBeamAI] Access token from ls.authData (web-connect-service). For Bearer injection in API calls. */
export function getAccessTokenFromStorage(): string {
  try {
    const json = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_DATA_KEY) : null;
    if (!json) return '';
    const data = JSON.parse(json);
    return data?.accessToken || data?.token || '';
  } catch {
    return '';
  }
}

/** [CurveBeamAI] session_id from the current URL, or null when absent. */
export function getUrlSessionId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('session_id');
  } catch {
    return null;
  }
}

/**
 * [CurveBeamAI] Remove stored auth ONLY when it belongs to a DIFFERENT session.
 *
 * `ls.authData` is localStorage, so it is shared by every viewer tab on this origin, and
 * `getAccessTokenFromStorage()` re-reads it on every request. Clearing it unconditionally therefore
 * breaks sibling tabs of the same DMP session mid-flight:
 *   1. their in-flight image requests go out with no Authorization header and AWS HealthImaging
 *      returns 403 — and a failed frame is not retried, so those images stay broken until reload;
 *   2. within 60s their token-validation interval (web-connect's token-management.service) finds no
 *      token and closes their WebSocket, which then never reconnects (close code 1000 with no
 *      reason returns silently), so they also stop receiving broadcasts such as `idleTimeout`.
 * A slow-loading tab has a wide exposure window, which is why the symptom looks intermittent and
 * tab-specific even though the token itself is shared and correct.
 *
 * Passing no `currentSessionId` (e.g. a tab opened without `session_id`) is treated as "this tab has
 * no claim on the shared token" and clears nothing — an unattributable tab must not evict auth that
 * healthy tabs are using. Keeping a same-session token is safe: web-connect's `isFetchTokenNeeded()`
 * validates it against `tokenValidationEndpoint` and only skips the fetch when it is still valid.
 */
export function clearAuthDataIfForeignSession(currentSessionId?: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const authJson = localStorage.getItem(AUTH_DATA_KEY);
    if (!authJson) return;

    const storedSessionId = JSON.parse(authJson)?.sessionId;
    // No recorded session, or this tab cannot attribute the token to itself → leave it alone.
    if (!storedSessionId || !currentSessionId) return;
    // Belongs to this session → sibling tabs are relying on it.
    if (storedSessionId === currentSessionId) return;

    localStorage.removeItem(AUTH_DATA_KEY);
  } catch (e) {
    console.warn('[AccessValidation] Could not inspect/clear localStorage authData:', e);
  }
}

/** [CurveBeamAI] Clear stored original params (e.g. after redirect to invalid-source for fresh DMP launch) */
export function clearOriginalQueryParams(): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(ORIGINAL_QUERY_PARAMS_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** [CurveBeamAI] Build a page path from routerBasename */
function withRouterBasename(path: string): string {
  const base = (typeof window !== 'undefined' && (window as any).config?.routerBasename) || '/';
  const normalized = base.endsWith('/') ? base.slice(0, -1) : base;
  return normalized ? `${normalized}${path}` : path;
}

/** [CurveBeamAI] Build invalid-source path from routerBasename */
export function getInvalidSourcePath(): string {
  return withRouterBasename(INVALID_SOURCE_PATH);
}

/** [WTD-1826] Build session-expired path from routerBasename */
export function getSessionExpiredPath(): string {
  return withRouterBasename(SESSION_EXPIRED_PATH);
}

/**
 * [WTD-1826] Build /config-error path from routerBasename.
 *
 * Exported because entryBootstrap and config-loader each carried their own copy of this function,
 * character for character, each re-deriving routerBasename and each hardcoding the path segment
 * that CONFIG_ERROR_PATH already holds. The other two pages have had a builder here all along; this
 * one did not, which is the only reason the copies existed.
 */
export function getConfigErrorPath(): string {
  return withRouterBasename(CONFIG_ERROR_PATH);
}

/**
 * [WTD-1826] Params excluded from the tampering check in EVERY version.
 *
 * `launch_code` is a single-use credential, valid for about thirty seconds, that DMP puts in the
 * launch URL and this application removes from the address bar as soon as it has been redeemed.
 * It is therefore not part of what makes a URL the same URL, and comparing it would break the
 * launch outright: validateAccess() records the original params BEFORE the code is exchanged or
 * stripped, so the next comparison would see it missing and report tampering on a perfectly
 * ordinary reload.
 *
 * Excluding it does not weaken the check. Every other parameter stays strictly compared, and the
 * code cannot be replayed anyway - it is spent the first time it is exchanged.
 */
const ALWAYS_IGNORED_PARAMS = ['launch_code'];

/** Params excluded from tampering check — the above, plus more in the 'future' version. */
function getIgnoredParams(): string[] {
  return isFutureVersion()
    ? [...ALWAYS_IGNORED_PARAMS, 'ImageSetID', 'workflow']
    : [...ALWAYS_IGNORED_PARAMS];
}

/** Filter out ignored params from a record */
function filterParams(obj: Record<string, string>): Record<string, string> {
  const ignored = getIgnoredParams();
  if (ignored.length === 0) return obj;
  return Object.fromEntries(Object.entries(obj).filter(([k]) => !ignored.includes(k)));
}

/**
 * [WTD-1826] The launch code from the current URL, or null.
 *
 * Read here rather than in the session service so the one place that knows the parameter's name
 * also owns removing it from the address bar - see stripLaunchCodeFromUrl.
 */
export function getUrlLaunchCode(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('launch_code');
  } catch {
    return null;
  }
}

/**
 * [WTD-1826] Removes `launch_code` from the address bar once it has been used.
 *
 * It is a bearer credential and single-use, so it should not sit in history, bookmarks or a copied
 * URL. Safe to do with history.replaceState here, unlike in the Angular consumers: there is no
 * router that will re-serialise the URL it parsed at bootstrap and put the parameter back.
 *
 * The tampering check does not see this change, because launch_code is excluded from it in every
 * version - see ALWAYS_IGNORED_PARAMS.
 */
export function stripLaunchCodeFromUrl(): void {
  try {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('launch_code')) return;
    url.searchParams.delete('launch_code');
    window.history.replaceState({}, '', url.toString());
  } catch (e) {
    console.warn('[AccessValidation] Could not remove launch_code from the URL:', e);
  }
}

/** Normalize params to sorted object for stable comparison; excludes ignored params */
function paramsToObject(params: URLSearchParams): Record<string, string> {
  const obj = Object.fromEntries(params.entries());
  const filtered = filterParams(obj);
  return Object.fromEntries(Object.entries(filtered).sort(([a], [b]) => a.localeCompare(b)));
}

/** Compare two param objects; returns true if keys and values match */
function paramsEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length) return false;
  return keysA.every(k => keysB.includes(k) && a[k] === b[k]);
}

/**
 * [CurveBeamAI] Validate access – viewer must be opened from a valid source.
 * Detects referrer violations and URL tampering (query params modified after first load).
 *
 * Flow:
 * 1. Referrer: in prod with whitelist, referrer must match.
 * 2. session_id: required in URL.
 * 3. Session change: storedSessionId ≠ urlSessionId → if referrer valid = new DMP launch (clear & allow), else tampering.
 * 4. Same session: compare current query params with stored; any diff = tampering.
 *
 * @returns true if valid; false → caller should redirect to invalid-source.
 */
export function validateAccess(): boolean {
  // 1. Referrer check (skipped in dev; same design as bmd-frontend)
  const currentReferrer = typeof document !== 'undefined' ? document.referrer : '';
  const referrerWhitelist = environment?.referrerWhitelist;
  const whitelist: string[] = Array.isArray(referrerWhitelist)
    ? referrerWhitelist
    : referrerWhitelist
      ? [referrerWhitelist]
      : [];
  const hasWhitelist = process.env.NODE_ENV !== 'development' && whitelist.length > 0;
  const isReferrerValid = !hasWhitelist || whitelist.includes(currentReferrer);

  if (hasWhitelist && !isReferrerValid) {
    console.warn('[AccessValidation] Invalid referrer:', { referrer: currentReferrer, whitelist });
    return false;
  }

  // 2. session_id required
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const urlSessionId = params?.get('session_id');
  if (!urlSessionId) {
    console.warn('[AccessValidation] Missing session_id in URL');
    return false;
  }

  const currentParamsObj = params ? paramsToObject(params) : {};

  if (typeof window === 'undefined') return true;

  try {
    const authJson = localStorage.getItem(AUTH_DATA_KEY);
    const authData = authJson ? JSON.parse(authJson) : null;
    const storedSessionId = authData?.sessionId;

    // 3. Session mismatch: new launch (valid referrer) vs tampering (invalid referrer)
    if (storedSessionId && storedSessionId !== urlSessionId) {
      if (isReferrerValid) {
        localStorage.removeItem(AUTH_DATA_KEY);
        sessionStorage.removeItem(ORIGINAL_QUERY_PARAMS_KEY);
      } else {
        console.warn('[AccessValidation] URL tampering – session_id mismatch');
        return false;
      }
    }

    // 4. Tampering check: params must match stored original.
    //    'future' version: skip check entirely (URL changes are allowed for development/testing).
    //    All other versions: strict comparison — no parameter may be changed.
    if (isFutureVersion()) {
      if (!sessionStorage.getItem(ORIGINAL_QUERY_PARAMS_KEY)) {
        sessionStorage.setItem(ORIGINAL_QUERY_PARAMS_KEY, JSON.stringify(currentParamsObj));
      }
    } else {
      const storedParamsJson = sessionStorage.getItem(ORIGINAL_QUERY_PARAMS_KEY);
      if (storedParamsJson) {
        const storedParams = filterParams(JSON.parse(storedParamsJson) as Record<string, string>);
        if (!paramsEqual(currentParamsObj, storedParams)) {
          console.warn('[AccessValidation] URL tampering – params modified', {
            stored: Object.keys(storedParams),
            current: Object.keys(currentParamsObj),
          });
          return false;
        }
      } else {
        sessionStorage.setItem(ORIGINAL_QUERY_PARAMS_KEY, JSON.stringify(currentParamsObj));
      }
    }

    return true;
  } catch {
    /* ignore parse/storage errors */
    return true; // Fail open to avoid blocking legitimate loads
  }
}

/** [CurveBeamAI] True when pathname is config-error (with or without basename) */
export function isOnConfigErrorPage(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.includes(CONFIG_ERROR_PATH);
}

/** [CurveBeamAI] True when pathname is invalid-source (with or without basename) */
export function isOnInvalidSourcePage(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p.includes(INVALID_SOURCE_PATH);
}

/** [WTD-1826] Whether we are on the session-expired page. See isOnSessionPage for why it matters. */
export function isOnSessionExpiredPage(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p.includes(SESSION_EXPIRED_PATH);
}

/**
 * [WTD-1826] Whether we are on ANY page rendered instead of the viewer.
 *
 * One predicate because asking this question in several places, each with its own list of paths,
 * is what broke the session-expired page on the day it was added: entryBootstrap did not recognise
 * the new path, ran its access check on it, found no session - correctly, the session had just
 * ended - and redirected to invalid-source. The viewer bounced straight off the page it had been
 * sent to, and the user saw "please open this from DMP" for a session that had simply timed out.
 *
 * Anything added here must be added to this function, not beside it.
 */
export function isOnSessionPage(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return (
    p.includes(CONFIG_ERROR_PATH) ||
    p.includes(INVALID_SOURCE_PATH) ||
    p.includes(SESSION_EXPIRED_PATH)
  );
}
